import React, { useState } from 'react';
import { useProducts } from '../../context/ProductsContext';
import { useCalculator } from '../../context/CalculatorContext';
import { useToast } from '../Common/Toast';
import { HOSPITAL_DEPARTMENTS } from '../../utils/calculations';
import Input from '../Common/Input';
import Button from '../Common/Button';
import Card from '../Common/Card';

const ProductForm = () => {
  const { addProduct } = useProducts();
  const { inputs } = useCalculator();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    baseHours: '',
    comp: '',
    risk: '',
    env: '',
    category: ''
  });
  const [loading, setLoading] = useState(false);
  const [showScoringHelp, setShowScoringHelp] = useState(false);
  
  const isHospital = inputs.facilityType === 'hospital';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.baseHours) {
      showToast('Please enter a Product Name and Base Annual Hours', 'error');
      return;
    }

    if (parseFloat(formData.baseHours) <= 0) {
      showToast('Base Annual Hours must be greater than 0', 'error');
      return;
    }

    setLoading(true);
    const result = await addProduct({
      name: formData.name,
      baseHours: parseFloat(formData.baseHours) || 0,
      comp: parseFloat(formData.comp) || 0,
      risk: parseFloat(formData.risk) || 0,
      env: parseFloat(formData.env) || 0,
      category: formData.category || null
    });

    setLoading(false);

    if (result.success) {
      showToast(`Product "${formData.name}" added successfully!`);
      setFormData({
        name: '',
        baseHours: '',
        comp: '',
        risk: '',
        env: '',
        category: ''
      });
    } else {
      showToast('Failed to add product. Please try again.', 'error');
    }
  };

  return (
    <Card title="➕ Add New Activity">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Activity Name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Incident Investigation"
          required
        />

        <Input
          label="Base Annual Hours"
          type="number"
          name="baseHours"
          value={formData.baseHours}
          onChange={handleChange}
          placeholder="e.g., 120"
          required
        />

        {isHospital && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hospital Department/Category
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No specific category</option>
              {Object.entries(HOSPITAL_DEPARTMENTS).map(([key, dept]) => (
                <option key={key} value={key}>
                  {dept.label} (Risk: {dept.risk})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-600 mt-1">
              Selecting a category applies hospital-specific risk multipliers
            </p>
          </div>
        )}

        {/* Scoring Guide Button */}
        <button
          type="button"
          onClick={() => setShowScoringHelp(true)}
          className="w-full px-4 py-2 border-2 border-gray-300 hover:border-blue-500 bg-white hover:bg-blue-50 text-gray-700 hover:text-blue-700 rounded-lg transition-colors font-medium text-sm mb-4"
        >
          📊 How to Score Activities
        </button>

        {/* Scoring Help Popup */}
        {showScoringHelp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowScoringHelp(false)}>
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <span className="text-2xl mr-2">📊</span>
                  Quick Scoring Reference
                </h3>
                <button
                  onClick={() => setShowScoringHelp(false)}
                  className="text-white hover:text-gray-200 transition-colors text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Complexity */}
                  <div className="bg-white rounded-lg border-2 border-blue-300 shadow-sm">
                    <div className="bg-blue-100 px-4 py-3 border-b-2 border-blue-300">
                      <div className="font-bold text-blue-900">Complexity % (0-100)</div>
                      <div className="text-xs text-blue-700 mt-1">Technical difficulty & expertise</div>
                    </div>
                    <div className="p-4">
                      <ul className="space-y-3 text-sm text-gray-700">
                        <li>
                          <div className="font-semibold text-gray-900 mb-1">• Low (0-25%)</div>
                          <div className="text-xs text-gray-600 ml-4">Simple, routine tasks</div>
                        </li>
                        <li>
                          <div className="font-semibold text-gray-900 mb-1">• Medium (26-60%)</div>
                          <div className="text-xs text-gray-600 ml-4">Moderate expertise needed</div>
                        </li>
                        <li>
                          <div className="font-semibold text-gray-900 mb-1">• High (61-100%)</div>
                          <div className="text-xs text-gray-600 ml-4">Highly technical, advanced certs required</div>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Risk */}
                  <div className="bg-white rounded-lg border-2 border-orange-300 shadow-sm">
                    <div className="bg-orange-100 px-4 py-3 border-b-2 border-orange-300">
                      <div className="font-bold text-orange-900">Risk % (0-100)</div>
                      <div className="text-xs text-orange-700 mt-1">Consequence severity</div>
                    </div>
                    <div className="p-4">
                      <ul className="space-y-3 text-sm text-gray-700">
                        <li>
                          <div className="font-semibold text-gray-900 mb-1">• Low (0-25%)</div>
                          <div className="text-xs text-gray-600 ml-4">Minor injuries only</div>
                        </li>
                        <li>
                          <div className="font-semibold text-gray-900 mb-1">• Medium (26-60%)</div>
                          <div className="text-xs text-gray-600 ml-4">Recordable injuries possible</div>
                        </li>
                        <li>
                          <div className="font-semibold text-gray-900 mb-1">• High (61-100%)</div>
                          <div className="text-xs text-gray-600 ml-4">Fatality potential, catastrophic incidents</div>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Environment */}
                  <div className="bg-white rounded-lg border-2 border-green-300 shadow-sm">
                    <div className="bg-green-100 px-4 py-3 border-b-2 border-green-300">
                      <div className="font-bold text-green-900">Environment % (0-100)</div>
                      <div className="text-xs text-green-700 mt-1">Environmental compliance burden</div>
                    </div>
                    <div className="p-4">
                      <ul className="space-y-3 text-sm text-gray-700">
                        <li>
                          <div className="font-semibold text-gray-900 mb-1">• Low (0-25%)</div>
                          <div className="text-xs text-gray-600 ml-4">Minimal environmental requirements</div>
                        </li>
                        <li>
                          <div className="font-semibold text-gray-900 mb-1">• Medium (26-60%)</div>
                          <div className="text-xs text-gray-600 ml-4">Some permits/reporting required</div>
                        </li>
                        <li>
                          <div className="font-semibold text-gray-900 mb-1">• High (61-100%)</div>
                          <div className="text-xs text-gray-600 ml-4">Multiple complex permits, major release potential</div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Formula */}
                <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <div className="font-semibold text-gray-900 mb-2">Formula:</div>
                  <div className="font-mono text-sm text-gray-700">
                    Weighted Hours = Base Hours × (1 + Complexity/100 + Risk/100 + Environment/100)
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Input
              label="Complexity %"
              type="number"
              name="comp"
              value={formData.comp}
              onChange={handleChange}
              placeholder="0-100"
              min="0"
              max="100"
            />
          </div>

          <div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Risk<br />%
              </label>
              <input
                type="number"
                name="risk"
                value={formData.risk}
                onChange={handleChange}
                placeholder="0-100"
                min="0"
                max="100"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              />
            </div>
          </div>

          <div>
            <Input
              label="Environment %"
              type="number"
              name="env"
              value={formData.env}
              onChange={handleChange}
              placeholder="0-100"
              min="0"
              max="100"
            />
          </div>
        </div>

        <div className="text-sm text-gray-600 bg-blue-50 p-4 rounded-lg leading-relaxed">
          <strong>Formula:</strong> Base Hours × (1 + Comp/100 + Risk/100 + Env/100)
          <p className="text-xs mt-2 text-gray-600">Click "How to Score Activities" above for detailed scoring criteria</p>
        </div>

        <Button type="submit" fullWidth disabled={loading}>
          {loading ? 'Adding...' : 'Add Activity'}
        </Button>
      </form>
    </Card>
  );
};

export default ProductForm;
